import React, { FunctionComponent, useCallback, useMemo } from "react"
import { useFormContext } from "react-hook-form"
import { useHistory, useRouteMatch } from "react-router"
import { Link } from "react-router-dom"
import DotButton from "../../components/Buttons/DotButton"
import { eImageVersions } from "../../definitions/Image"
import { useImage } from "../../hooks/useImage"
import { useImageUrl } from "../../hooks/useImageUrl"
import { getExhibitionRoutes } from "./inspector/routes"

interface iProps {
  cover?: string
  title: string
  handlePublish?: () => void
  handleSubmit?: () => void
  handleDelete?: () => void
  editorLink?: string
  visitLink?: string
  submitEditorText: string
  submitVisitText: string
  submitPublishText: string
  submitDeleteText: string
  submitText?: string
  published?: boolean
  isLoading?: boolean
  type?: "create" | "edit"
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  access?: any
}

const ExhibitionHeader: FunctionComponent<iProps> = ({
  cover = "",
  title,
  handlePublish,
  handleSubmit,
  handleDelete,
  editorLink,
  visitLink,
  submitEditorText,
  submitVisitText,
  submitPublishText,
  submitDeleteText,
  submitText = "Update",
  published,
  isLoading = false,
  type = "edit",
  access,
}) => {
  const match = useRouteMatch()
  const history = useHistory()

  const handleHisoryBack = useCallback((e: React.SyntheticEvent) => {
    e.preventDefault()
    history.goBack()
  }, [])

  const navRoutes = useMemo(
    () => getExhibitionRoutes({ urlPrefix: match.url }),
    [match.url],
  )
  const { file } = useImage({
    fileId: cover,
    fileCollection: "Files_ExhibitionPosters",
  })

  const coverImage =
    file && useImageUrl(eImageVersions.XLWRegular, file.versions)

  const {
    formState: { dirtyFields },
  } = useFormContext()

  const keys = Object.keys(dirtyFields)

  return (
    <div className="inspector-header">
      <div className="inspector-cover-wrap">
        <div
          className="inspector-cover"
          style={{ backgroundImage: `url(${coverImage})` }}
        ></div>
        <div className="inspector-cover-overlay">
          <div className="inspector-headline align-items-start">
            <Link to={""} onClick={handleHisoryBack}>
              <i className="fa fa-chevron-left"></i>
              <span>Back</span>
            </Link>
            <div className="d-flex">
              {type === "edit" && (
                <>
                  {access && access.canDelete && (
                    <DotButton
                      isLoading={isLoading}
                      className={`btn-red submit-button`}
                      text={submitDeleteText}
                      onClick={handleDelete}
                      type="button"
                    >
                      {submitDeleteText}
                    </DotButton>
                  )}
                  <DotButton
                    isLoading={isLoading}
                    text={submitPublishText}
                    className={`btn-blue submit-button`}
                    onClick={handlePublish}
                    type="button"
                  >
                    {submitPublishText}
                  </DotButton>
                  {visitLink && (
                    <a
                      href={visitLink}
                      target="_blank"
                      rel="noreferrer"
                      className="button btn-blue submit-button"
                    >
                      {submitVisitText}
                    </a>
                  )}
                  {editorLink && (
                    <a
                      href={editorLink}
                      target="_blank"
                      rel="noreferrer"
                      className="button btn-yellow submit-button"
                    >
                      {submitEditorText}
                    </a>
                  )}
                </>
              )}
              {(type === "create" || (access && access.canEdit)) && (
                <DotButton
                  className={`btn-blue submit-button`}
                  text={submitText}
                  isLoading={isLoading}
                  onClick={handleSubmit}
                  type="button"
                >
                  {submitText}
                </DotButton>
              )}
            </div>
          </div>
          <h1 className="inspector-title">{title}</h1>
        </div>
      </div>
      {published !== undefined ? (
        published ? (
          <div className="published">This Exhibition is published!</div>
        ) : (
          <div className="draft">This Exhibition is a draft!</div>
        )
      ) : null}
      <div className="inspector-navigation">
        <ul>
          {navRoutes.map((n, i) => {
            return (
              <li
                key={i}
                className={
                  history.location.pathname === n.route ? "active" : ""
                }
              >
                <Link to={n.route} replace>
                  <span>
                    {n.title}{" "}
                    {keys.find((k) => n.fields?.includes(k)) && <p></p>}
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

export default ExhibitionHeader
